import React, { useEffect } from 'react';
import {
  ActionIcon,
  AppShell,
  Avatar,
  Divider,
  Grid,
  Group,
  NavLink,
  Stack,
  Text
} from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { HiLogout, HiOutlineUserCircle, HiOutlineViewGrid } from 'react-icons/hi';
import { useAuth } from 'hooks/useAuth';
import Brands from './Brands';
import styles from './styles.module.css';
import MENU, { isValidMenuKey } from 'constant/menu';
import { useAppName } from 'contexts/AppProvider';
import ModalLogout from 'modules/pages/dashboard/ModalLogout';
import { useDisclosure } from '@mantine/hooks';
import { getParentMenuUrl } from 'utils/getParentMenuUrl';
import { IconX } from '@tabler/icons-react';

interface SidebarProps {
  onMobileNavClose?: () => void;
  mobileNavOpened?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ onMobileNavClose, mobileNavOpened }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [opened, { open, close }] = useDisclosure(false);
  const { setAppName } = useAppName();
  const { logout, profile } = useAuth();

  const notEntryPoint = location.pathname.split('/')[1];
  const getAppName = location.pathname.split('/')[2];
  const getMenu = isValidMenuKey(getAppName) ? MENU[getAppName] : null;

  const userRole = 'superadmin';

  useEffect(() => {
    if (isValidMenuKey(getAppName)) {
      setAppName(getAppName);
    }
  }, [getAppName, setAppName]);

  if (!getMenu && notEntryPoint) return null;

  const handleLogout = () => logout();

  const handleNavigation = (url: string) => {
    navigate(url);
    onMobileNavClose?.();
  };

  return (
    <>
      <AppShell.Navbar px={{ base: 16, md: 8 }} py={{ base: 16, md: 40 }}>
        <Grid>
          <Grid.Col span={{ base: 6 }}>
            <Brands />
          </Grid.Col>
          {mobileNavOpened && (
            <Grid.Col hiddenFrom='md' span={{ base: 6 }}>
              <Stack justify='flex-end' align='flex-end'>
                <ActionIcon variant='transparent' onClick={onMobileNavClose}>
                  <IconX />
                </ActionIcon>
              </Stack>
            </Grid.Col>
          )}
        </Grid>
        {notEntryPoint &&
          getMenu &&
          getMenu.map((menu, i) => {
            if (userRole && !menu.role.includes(userRole)) return null;

            if (menu.name === 'Divider') return <Divider key={`menu-${i}`} />;
            if (menu.children.length > 0) {
              return (
                <React.Fragment key={`menu-${i}`}>
                  <NavLink
                    className={styles.menuItem}
                    label={menu.name}
                    leftSection={<menu.icon size='1rem' />}
                    onClick={() => handleNavigation(menu.url)}
                    active={location.pathname === menu.url}
                  >
                    {menu.children.map((child, index) => {
                      if (userRole && !child.role.includes(userRole)) return null;

                      return (
                        <NavLink
                          className={styles.menuItem}
                          key={`${menu.name}-child-${index}`}
                          label={child.name}
                          pl={40}
                          onClick={() => handleNavigation(child.url)}
                          active={location.pathname === child.url}
                        />
                      );
                    })}
                  </NavLink>
                </React.Fragment>
              );
            }

            const splitMenuUrl = menu.url.split('/');
            const getLastMenuUrl = splitMenuUrl[splitMenuUrl.length - 1];

            const activeMenu = getLastMenuUrl === getParentMenuUrl();
            return (
              <NavLink
                className={styles.menuItem}
                key={`menu-${i}`}
                label={menu.name}
                leftSection={<menu.icon size='1rem' />}
                onClick={() => handleNavigation(menu.url)}
                active={activeMenu}
              />
            );
          })}
        <Stack h={'100%'} gap={4} justify='flex-end'>
          {profile && (
            <>
              <Group px={12} gap={12} mb={8}>
                <Avatar color='gray' size={'sm'} radius='sm'>
                  <HiOutlineUserCircle />
                </Avatar>
                <Stack w={160} gap={0}>
                  <Text fz={14} fw={600} truncate>
                    {profile?.adminName || 'User'}
                  </Text>
                  <Text fz={12} fw={400} c={'gray'} truncate>
                    {profile?.adminEmail || 'User'}
                  </Text>
                </Stack>
              </Group>
              <Divider />
            </>
          )}
          {notEntryPoint && (
            <NavLink
              className={styles.menuItem}
              label='More Apps'
              leftSection={<HiOutlineViewGrid />}
              onClick={() => handleNavigation('/')}
              active={location.pathname === '/'}
            />
          )}{' '}
          <NavLink
            label='Logout'
            c={'destructive'}
            leftSection={<HiLogout />}
            onClick={() => open()}
          />
        </Stack>
      </AppShell.Navbar>
      <ModalLogout opened={opened} onConfirm={handleLogout} onClose={() => close()} />
    </>
  );
};

export default Sidebar;
